import { Position, Feature, Polygon, bboxPolygon } from '@turf/turf';
import { config } from './config/config';
import { getCssParsedValue } from './css-utils';
import { STREETS_BROWSING_H3_RES } from './stores/streets-store.consts';
import { AppDisplaySettings } from './types';
import { ReactComponent as LiveIcon } from '../assets/icons/nav_menu_links/nav-menu-link-live-feed.svg';
import { ReactComponent as LiveIconFull } from '../assets/icons/nav_menu_links/nav-menu-link-live-feed-full.svg';
import { ReactComponent as CzdmIcon } from '../assets/icons/nav_menu_links/nav-menu-link-czdm.svg';
import { ReactComponent as CzdmIconFull } from '../assets/icons/nav_menu_links/nav-menu-link-czdm-full.svg';
import { ReactComponent as RidmIcon } from '../assets/icons/nav_menu_links/nav-menu-link-ridm.svg';
import { ReactComponent as RidmIconFull } from '../assets/icons/nav_menu_links/nav-menu-link-ridm-full.svg';
import { ReactComponent as CctvIcon } from '../assets/icons/nav_menu_links/nav-menu-link-vcctv.svg';
import { ReactComponent as CctvIconFull } from '../assets/icons/nav_menu_links/nav-menu-link-vcctv-full.svg';

export const FRAME_PATH = 'frame';
export const ROAD_SIGN_PATH = 'sign';
export const SUBSCRIPTION_PATH = 'subscription';
export const HEXSEG_PATH = 'hexseg';
export const TERMS_PATH = 'terms';
export const CENTER_QUERY_PARAM = 'center';
export const ZOOM_QUERY_PARAM = 'zoom';
export const FILTERS_QUERY_PARAM = 'filters';

export const BASE_DOCUMENT_TITLE: string = 'CityStream™';

export const CS_HD_FRAMES_BASE_URL: string = 'https://images-cdn-api.getnexar.com/aod/full/';

export enum APP_MODES {
  'CZDM' = 'CZDM',
  'RTCZDM' = 'RTCZDM',
  'RIDM' = 'RIDM',
  'CCTV' = 'CCTV',
  'MTBV' = 'MTBV',
  'LIVE' = 'LIVE',
  'HXSG' = 'HXSG',
  'STREETS' = 'STREETS',
}

export enum APP_BASE_PATHNAMES {
  'CZDM' = '/czdm',
  'RTCZDM' = '/rt-czdm',
  'RIDM' = '/ridm',
  'CCTV' = '/virtualcam',
  'MTBV' = '/mtbv',
  'LIVE' = '/livefeed',
  'HXSG' = '/streets',
  'STREETS' = '/',
}

export enum APP_OKTA_API_GROUPS {
  'LIVE' = 'livefeed-product-api',
  'CZDM' = 'workzones-product-api',
  'RIDM' = 'roadinventory-product-api',
  'CCTV' = 'virtualcam-product-api',
}

export enum APP_OKTA_UI_GROUPS {
  'LIVE' = 'livefeed-product-ui',
  'CZDM' = 'workzones-product-ui',
  'RIDM' = 'roadinventory-product-ui',
  'CCTV' = 'virtualcam-product-ui',
}

export const APP_DISPLAY_SETTINGS: { [type: string]: AppDisplaySettings } = {
  CZDM: {
    label: 'Work Zones',
    iconComp: CzdmIcon,
    iconFullComp: CzdmIconFull,
  },
  RIDM: {
    label: 'Road Inventory',
    iconComp: RidmIcon,
    iconFullComp: RidmIconFull,
  },
  CCTV: {
    label: 'Virtual Camera',
    iconComp: CctvIcon,
    iconFullComp: CctvIconFull,
  },
  LIVE: {
    label: 'Live Feed',
    iconComp: LiveIcon,
    iconFullComp: LiveIconFull,
  },
};

export const OKTA_PERMITTED_AREAS: { [id: string]: [Position, Position] } = {
  canada: [
    [-141, 41.68],
    [-52.63, 83.11],
  ],
  all_us: [
    [-130.78917857966928, 22.523177693077468],
    [-60.04987624466713, 52.01765147335175],
  ],
  las_vegas: [
    [-115.47927309981365, 35.86232543420575],
    [-114.82910269179216, 36.390364204759614],
  ],
  oregon: [
    [-125.53593743340423, 41.541306767798204],
    [-115.51066588877956, 46.575353599916525],
  ],
  utah: [
    [-114.56577241413757, 36.49151987185361],
    [-108.5449986009319, 42.510744252043224],
  ],
  israel: [
    [33.97393938218841, 28.86590241934751],
    [36.219697994895135, 33.821466222091054],
  ],
  latvia: [
    [20.08488441998646, 55.34442727042267],
    [29.057749239476287, 58.38651024537771],
  ],
  lithuania: [
    [20.07743236646264, 53.43810960623903],
    [27.620654395474833, 56.83553740797007],
  ],
  bucharest: [
    [25.905246915760394, 44.288082256563484],
    [26.28763999747408, 44.57378846535835],
  ],
  new_york: [
    [-74.3111492, 40.454194099999995],
    [-73.6444508, 40.9574729],
  ],
  detroit: [
    [-84.44091796875, 41.290189955885644],
    [-82.1502685546875, 43.12504316740127],
  ],
  all_europe: [
    [-22.38, 32.43],
    [49.59, 75.38],
  ],
  texas: [
    [-106.836, 25.694],
    [-92.7983, 36.693],
  ],
  los_angeles: [
    [-118.959, 33.261],
    [-117.389, 34.844],
  ],
  all_japan: [
    [127.209401, 25.215737],
    [151.404436, 46.496389],
  ],
  austin: [
    [-97.94, 30.149],
    [-97.553, 30.428],
  ],
  all_australia: [
    [109.38319, -48.87316],
    [179.67161, -10.15478],
  ],
  all_africa: [
    [-25.92, -35.07],
    [60.12, 37.45],
  ],
  india: [
    [68.22, 7.85],
    [97.24, 35.75],
  ],
  hawaii: [
    [-160.56, 18.784],
    [-154.708, 22.297],
  ],
  japan_kddi: [
    [139.2981015343812, 35.64822874218713],
    [139.49087465476865, 35.70970922248592],
  ],
  las_vegas_motional: [
    [-115.18047986746426, 36.06460439362405],
    [-115.1449270073341, 36.11532603915627],
  ],
  bay_area: [
    [-122.6419362847453, 37.1859719327645],
    [-121.63495678956399, 38.214893657605984],
  ],
  uk_ireland: [
    [-10.535193078226285, 49.80360854082875],
    [2.399338840392062, 60.89803656542048],
  ],
  alaska: [
    [-179.148909, 51.214183],
    [-129.9795, 71.365162],
  ],
  pittsburgh: [
    [-80.0956, 40.361],
    [-79.798, 40.5017],
  ],
  aveiro: [
    [-8.777830143990286, 40.55556227406541],
    [-8.516916111837816, 40.69900369882899],
  ],
  portugal: [
    [-9.5, 36.9],
    [-6.19, 42.15],
  ],
  dallas: [
    [-97.09151555291815, 32.57381611561259],
    [-96.49739543772434, 32.969231377598675],
  ],
};

export enum MAP_STYLES {
  Map = 'Map',
  MapHighContrast = 'MapHighContrast',
  Satellite = 'Satellite',
}

export enum VIEW_MODES {
  'CAM' = 'cam-view',
  'MAP' = 'map-view',
}

export const MAP_STYLES_URLS = {
  [MAP_STYLES.Map]: config.MAP_STYLE,
  [MAP_STYLES.MapHighContrast]: config.MAP_HIGH_CONTRAST_STYLE,
  [MAP_STYLES.Satellite]: config.SATELLITE_STYLE,
};

export enum SearchInputModes {
  DEFAULT = 'DEFAULT',
  LIVE = 'LIVE',
  CCTV = 'CCTV',
  HXSG = 'HXSG',
  MTBV = 'MTBV',
  STREETS = 'STREETS',
}

export enum MeasuringToolPopupCustomModes {
  Info = 'info',
  Error = 'error',
}

export enum JsControlledCssVariables {
  TRIMMED_FRAME_ASPECT_RATIO = '--trimmed-frame-aspect-ratio',
  LEAVING_TRIMMED_FRAME_ASPECT_RATIO = '--leaving-trimmed-frame-aspect-ratio',
}

export enum Classes {
  CS_APP_CONTAINER = 'cs-app-container',
  CS_MOBILE_DEVICE_NOTIFICATION = 'cs-mobile-device-notification',
}

//TODO - Oren - Deprecate KeyCodes in favor of KeyValues
export enum KeyCodes {
  LEFT_ARROW_KEYCODE = 37,
  UP_ARROW_KEYCODE = 38,
  RIGHT_ARROW_KEYCODE = 39,
  DOWN_ARROW_KEYCODE = 40,
  EQUAL_PLUS = 187,
  MINUS = 189,
  ESC_KEYCODE = 27,
}

export enum KeyValues {
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight',
  Escape = 'Escape',
}

export enum RegionCountries {
  'US' = 'United States',
}

export const WorldBounds: [Position, Position] = [
  [-180, -90],
  [180, 90],
];

export enum HdImageDimensions {
  Width = 1280,
  Height = 720,
}

export enum ThumbnailImageDimensions {
  Width = 160,
  Height = 90,
}

export const WINDOW_RESIZE_TRACKING_DEBOUNCE_MS: number = 320;

//@ts-ignore
export const StreetsSupportedBounds: [Position, Position][] = [
  STREETS_BROWSING_H3_RES === 10
    ? [
        [-74.039129866, 40.6398451133],
        [-73.9117637876, 40.7516865928],
      ]
    : null, // New York - Staging
  STREETS_BROWSING_H3_RES === 9
    ? [
        [-87.75222, 41.80715],
        [-87.54383, 41.9914],
      ]
    : null, // Chicago
].filter((val) => !!val);

export const StreetsSupportedBoundsPolygonFeatures: Feature<Polygon>[] = StreetsSupportedBounds.map((supportedBounds) =>
  bboxPolygon(supportedBounds.flat() as [number, number, number, number])
);

/* Dimensions */

export const InitValues = () => {
  Dimensions.FRAME_ASPECT_RATIO = getCssParsedValue('--frame-aspect-ratio');
  Dimensions.CROPPED_FRAME_ASPECT_RATIO = getCssParsedValue('--cropped-frame-aspect-ratio');
  Dimensions.CS_MOBILE_DEVICE_NOTIFICATION_HEIGHT_PX = getCssParsedValue('--cs-mobile-device-notification-height');
  Dimensions.SIDENAV_WIDTH_PX = getCssParsedValue('--sidenav-width');
  Dimensions.NARROW_EXTENDING_SIDENAV_CARD_WIDTH_PX = getCssParsedValue('--narrow-extending-sidenav-card-width');
  Dimensions.NARROW_COLLAPSED_SIDENAV_CARD_WIDTH_PX = getCssParsedValue('--narrow-collapsed-sidenav-card-width');
  Dimensions.EXPANDED_SIDENAV_WIDTH_PX = getCssParsedValue('--expanded-sidenav-width');
  Dimensions.CS_ICON_DIAMETER_PX = getCssParsedValue('--cs-icon-diameter');
  Dimensions.FRAMES_RAIL_HEIGHT = getCssParsedValue('--frames-rail-height');
  Dimensions.FRAMES_RAIL_BORDER_TOP_WIDTH_PX = getCssParsedValue('--frames-rail-border-top-width');
  Dimensions.RAIL_FRAME_WIDTH_PX = getCssParsedValue('--rail-frame-width');
  Dimensions.RAIL_FRAME_HEIGHT_PX = getCssParsedValue('--rail-frame-height');
  Dimensions.RAIL_FRAME_LEFT_MARGIN_PX = getCssParsedValue('--rail-frame-left-margin');
  Dimensions.HD_FRAME_SECTION_WIDTH = getCssParsedValue('--hd-frame-section-width');
  Dimensions.HD_FRAME_MODE_MIN_MAP_HEIGHT_PX = getCssParsedValue('--hd-frame-mode-min-map-height');
  Dimensions.HD_FRAME_MODE_EXTENDED_MIN_MAP_HEIGHT_PX = getCssParsedValue('--hd-frame-mode-extended-min-map-height');
  Dimensions.LIVE_FEED_HD_FRAME_INFO_WIDTH = getCssParsedValue('--live-feed-hd-frame-info-width');
  Dimensions.HD_FRAME_ANNOTATION_MODE_MIN_MAP_HEIGHT_PX = getCssParsedValue(
    '--hd-frame-annotation-mode-min-map-height'
  );
  Dimensions.HD_FRAME_MIN_CHANGE_DETECTION_VISIBLE_WIDTH_PORTION = getCssParsedValue(
    '--hd-frame-min-change-detection-visible-width-portion'
  );
  Dimensions.MEASURING_TOOL_INFO_VALUE_LINE_TOTAL_HEIGHT_PX = getCssParsedValue(
    '--measuring-tool-info-value-line-total-height'
  );
  Dimensions.MEASURING_TOOL_INFO_UNIT_LINE_TOTAL_HEIGHT_PX = getCssParsedValue(
    '--measuring-tool-info-unit-line-total-height'
  );
  Dimensions.MEASURING_TOOL_INFO_UNITS_LIST_VERTICAL_PADDING_PX = getCssParsedValue(
    '--measuring-tool-info-units-list-vertical-padding'
  );

  /* CCTV Map Section Start */
  Dimensions.CCTV_FRAME_ASPECT_RATIO = getCssParsedValue('--cctv-frame-aspect-ratio');
  Dimensions.SUBSCRIPTIONS_LIST_ITEM_TOTAL_HEIGHT = getCssParsedValue('--subscriptions-list-item-total-height');
  Dimensions.SUBSCRIPTIONS_LIST_ITEM_VERTICAL_MARGIN = getCssParsedValue('--subscriptions-list-item-vertical-margin');
  Dimensions.SUBSCRIPTIONS_LIST_REQUEST_LOCATION_TOTAL_HEIGHT = getCssParsedValue(
    '--subscriptions-list-request-location-total-height'
  );
  Dimensions.FRAME_TIMELINE_ITEM_MIN_WIDTH_PX = getCssParsedValue('--frame-timeline-item-min-width');
  Dimensions.FRAME_TIMELINE_ITEM_MAX_WIDTH_PX = getCssParsedValue('--frame-timeline-item-max-width');
  Dimensions.FRAME_TIMELINE_HORIZONTAL_MARGIN_PX = getCssParsedValue('--frame-timeline-hortizontal-margin');
  Dimensions.FRAME_TIMELINE_HEIGHT_PX = getCssParsedValue('--frame-timeline-height');
  Dimensions.FRAME_TIMELINE_SUBSCRIBE_TOTAL_WIDTH_PX = getCssParsedValue('--frame-timeline-subscribe-total-width');
  Dimensions.FRAMES_DASHBOARD_SCREEN_MARGIN = getCssParsedValue('--frames-dashboard-screen-margin');
  Dimensions.FRAMES_DASHBOARD_SCREEN_FRAME_BORDER_WIDTH = getCssParsedValue(
    '--frames-dashboard-screen-frame-border-width'
  );
  Dimensions.FRAMES_DASHBOARD_SCREEN_FRAME_PER_ROW = getCssParsedValue('--frames-dashboard-screen-frame-per-row');
  Dimensions.FRAMES_DASHBOARD_SCREEN_FRAME_PER_COLUMN = getCssParsedValue('--frames-dashboard-screen-frame-per-column');
  Dimensions.FRAMES_DASHBOARD_SCREEN_FRAME_PER_SCREEN = getCssParsedValue('--frames-dashboard-screen-frame-per-screen');
  Dimensions.CITIES_DRAWER_CLOSED_HEIGHT = getCssParsedValue('--cities-drawer-closed-height');
  Dimensions.CITIES_DRAWER_OPEN_MARGIN_BOTTOM = getCssParsedValue('--cities-drawer-open-margin-bottom');

  /* Streets Map Section Start */
  Dimensions.REF_IMAGE_THUMBNAIL_WIDTH_PX = getCssParsedValue('--ref-image-thumbnail-width');
  Dimensions.REF_IMAGE_THUMBNAIL_HEIGHT_PX = getCssParsedValue('--ref-image-thumbnail-height');
};

export const Dimensions: {
  FRAME_ASPECT_RATIO?: number;
  CROPPED_FRAME_ASPECT_RATIO?: number;
  CS_MOBILE_DEVICE_NOTIFICATION_HEIGHT_PX?: number;
  SIDENAV_WIDTH_PX?: number;
  NARROW_EXTENDING_SIDENAV_CARD_WIDTH_PX?: number;
  NARROW_COLLAPSED_SIDENAV_CARD_WIDTH_PX?: number;
  EXPANDED_SIDENAV_WIDTH_PX?: number;
  CS_ICON_DIAMETER_PX?: number;
  FRAMES_RAIL_HEIGHT?: number;
  FRAMES_RAIL_BORDER_TOP_WIDTH_PX?: number;
  RAIL_FRAME_WIDTH_PX?: number;
  RAIL_FRAME_HEIGHT_PX?: number;
  RAIL_FRAME_LEFT_MARGIN_PX?: number;
  HD_FRAME_SECTION_WIDTH?: number;
  HD_FRAME_MODE_MIN_MAP_HEIGHT_PX?: number;
  HD_FRAME_MODE_EXTENDED_MIN_MAP_HEIGHT_PX?: number;
  LIVE_FEED_HD_FRAME_INFO_WIDTH?: number;
  HD_FRAME_ANNOTATION_MODE_MIN_MAP_HEIGHT_PX?: number;
  MEASURING_TOOL_INFO_VALUE_LINE_TOTAL_HEIGHT_PX?: number;
  MEASURING_TOOL_INFO_UNIT_LINE_TOTAL_HEIGHT_PX?: number;
  MEASURING_TOOL_INFO_UNITS_LIST_VERTICAL_PADDING_PX?: number;
  HD_FRAME_MIN_CHANGE_DETECTION_VISIBLE_WIDTH_PORTION?: number;

  /* CCTV Map Section Start */
  CCTV_FRAME_ASPECT_RATIO?: number;
  SUBSCRIPTIONS_LIST_ITEM_TOTAL_HEIGHT?: number;
  SUBSCRIPTIONS_LIST_ITEM_VERTICAL_MARGIN?: number;
  SUBSCRIPTIONS_LIST_REQUEST_LOCATION_TOTAL_HEIGHT?: number;
  FRAME_TIMELINE_ITEM_MIN_WIDTH_PX?: number;
  FRAME_TIMELINE_ITEM_MAX_WIDTH_PX?: number;
  FRAME_TIMELINE_HORIZONTAL_MARGIN_PX?: number;
  FRAME_TIMELINE_HEIGHT_PX?: number;
  FRAME_TIMELINE_SUBSCRIBE_TOTAL_WIDTH_PX?: number;
  FRAMES_DASHBOARD_SCREEN_MARGIN?: number;
  FRAMES_DASHBOARD_SCREEN_FRAME_BORDER_WIDTH?: number;
  FRAMES_DASHBOARD_SCREEN_FRAME_PER_ROW?: number;
  FRAMES_DASHBOARD_SCREEN_FRAME_PER_COLUMN?: number;
  FRAMES_DASHBOARD_SCREEN_FRAME_PER_SCREEN?: number;
  CITIES_DRAWER_CLOSED_HEIGHT?: number;
  CITIES_DRAWER_OPEN_MARGIN_BOTTOM?: number;

  /* Streets Map Section Start */
  REF_IMAGE_THUMBNAIL_WIDTH_PX?: number;
  REF_IMAGE_THUMBNAIL_HEIGHT_PX?: number;
} = {};
